import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SocialSharing = ({ missionResult, achievements, onGitHubCommit }) => {
  const [isSharing, setIsSharing] = useState(false);
  const [shareMessage, setShareMessage] = useState('');

  const generateShareText = () => {
    const status = missionResult?.status === 'passed' ? '✅ Completed' : '🔄 Attempted';
    const score = missionResult?.score;
    const achievementText = achievements?.length > 0 
      ? `\n🏆 Unlocked ${achievements?.length} new achievement${achievements?.length > 1 ? 's' : ''}!`
      : '';
    
    return `${status} "${missionResult?.missionTitle}" on Selenium Sandbox!\n📊 Score: ${score}%${achievementText}\n\n#SeleniumAutomation #WebTesting #CodingChallenge`;
  };

  const handleShare = async (platform) => {
    setIsSharing(true);
    const shareText = generateShareText();
    const shareUrl = `${window.location?.origin}/mission-results-summary`;

    try {
      switch (platform) {
        case 'twitter':
          const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
          window.open(twitterUrl, '_blank', 'width=550,height=420');
          break;
          
        case 'linkedin':
          const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}&summary=${encodeURIComponent(shareText)}`;
          window.open(linkedinUrl, '_blank', 'width=550,height=420');
          break;
          
        case 'copy':
          await navigator.clipboard?.writeText(`${shareText}\n${shareUrl}`);
          setShareMessage('Link copied to clipboard!');
          setTimeout(() => setShareMessage(''), 3000);
          break;
          
        case 'github':
          await onGitHubCommit(missionResult, shareText);
          setShareMessage('Progress committed to GitHub!');
          setTimeout(() => setShareMessage(''), 3000);
          break;
          
        default:
          break;
      }
    } catch (error) {
      setShareMessage('Failed to share. Please try again.');
      setTimeout(() => setShareMessage(''), 3000);
    } finally {
      setIsSharing(false);
    }
  };

  const shareOptions = [
    {
      id: 'twitter',
      name: 'Twitter',
      icon: 'Twitter',
      color: 'bg-blue-500 hover:bg-blue-600',
      description: 'Share your achievement on Twitter'
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: 'Linkedin',
      color: 'bg-blue-700 hover:bg-blue-800',
      description: 'Share with your professional network'
    },
    {
      id: 'github',
      name: 'GitHub',
      icon: 'Github',
      color: 'bg-gray-800 hover:bg-gray-900',
      description: 'Commit your progress to GitHub'
    },
    {
      id: 'copy',
      name: 'Copy Link',
      icon: 'Copy',
      color: 'bg-muted hover:bg-muted/80',
      description: 'Copy shareable link to clipboard'
    }
  ];

  return (
    <div className="bg-card rounded-lg p-6 border border-subtle">
      <div className="flex items-center gap-3 mb-6">
        <Icon name="Share2" size={24} color="var(--color-primary)" />
        <h2 className="text-xl font-semibold text-foreground">Share Your Achievement</h2>
      </div>
      {/* Achievement Summary */}
      <div className="bg-muted/50 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            missionResult?.status === 'passed' ? 'bg-success/20' : 'bg-warning/20'
          }`}>
            <Icon 
              name={missionResult?.status === 'passed' ? 'CheckCircle' : 'Clock'} 
              size={24} 
              color={missionResult?.status === 'passed' ? 'var(--color-success)' : 'var(--color-warning)'}
            />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground">
              {missionResult?.missionTitle}
            </h3>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>Score: {missionResult?.score}%</span>
              <span>Time: {missionResult?.executionTime}</span>
              {achievements?.length > 0 && (
                <span className="text-warning">
                  +{achievements?.length} achievement{achievements?.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Share Preview */}
      <div className="mb-6">
        <h3 className="font-medium text-foreground mb-3">Share Preview:</h3>
        <div className="bg-muted/30 border border-subtle rounded-lg p-4">
          <pre className="text-sm text-foreground whitespace-pre-wrap font-sans">
            {generateShareText()}
          </pre>
        </div>
      </div>
      {/* Share Options */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {shareOptions?.map((option) => (
          <Button
            key={option?.id}
            variant="outline"
            className={`justify-start h-auto p-4 ${option?.color} text-white border-0`}
            disabled={isSharing}
            onClick={() => handleShare(option?.id)}
          >
            <div className="flex items-center gap-3">
              <Icon name={option?.icon} size={20} />
              <div className="text-left">
                <div className="font-medium">{option?.name}</div>
                <div className="text-xs opacity-80">{option?.description}</div>
              </div>
            </div>
          </Button>
        ))}
      </div>
      {/* Status Message */}
      {shareMessage && (
        <div className="flex items-center gap-2 p-3 bg-success/20 text-success rounded-lg">
          <Icon name="CheckCircle" size={16} />
          <span className="text-sm">{shareMessage}</span>
        </div>
      )}
      {/* GitHub Integration Info */}
      <div className="border-t border-subtle pt-6">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={16} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
          <div className="text-sm text-muted-foreground">
            <p className="mb-2">
              <strong className="text-foreground">GitHub Integration:</strong> 
              Share your progress directly to your GitHub profile to showcase your learning journey.
            </p>
            <ul className="space-y-1 text-xs">
              <li>• Creates a commit with your mission completion</li>
              <li>• Updates your learning progress repository</li>
              <li>• Includes code snippets and achievement badges</li>
              <li>• Builds your public coding portfolio</li>
            </ul>
          </div>
        </div>
      </div>
      {/* Social Stats */}
      <div className="mt-6 pt-6 border-t border-subtle">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-lg font-bold text-foreground">
              {missionResult?.socialStats?.shares || 0}
            </div>
            <div className="text-xs text-muted-foreground">Total Shares</div>
          </div>
          <div>
            <div className="text-lg font-bold text-foreground">
              {missionResult?.socialStats?.githubCommits || 0}
            </div>
            <div className="text-xs text-muted-foreground">GitHub Commits</div>
          </div>
          <div>
            <div className="text-lg font-bold text-foreground">
              {missionResult?.socialStats?.communityRank || 'N/A'}
            </div>
            <div className="text-xs text-muted-foreground">Community Rank</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialSharing;